import React from 'react'

import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import { ButtonWithIcon } from '../Buttons'
import { KeyboardArrowDownIcon } from '../svgIcons'

const DatePickerDropdownContainer = styled.div`
  position: relative;
`

const StyledButtonWithIcon = styled(ButtonWithIcon)`
  width: 100%;
  height: 28px;
  border-radius: 4px;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 4px 4px 4px 8px;
`

const OptionsContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: 10;
  outline: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const Option = styled.div<{ $isSelected: boolean; $isFocused: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  text-align: left;
  color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.gray[0] : theme.colors.gray[80])};
  background-color: ${({ theme, $isSelected, $isFocused }) =>
    $isSelected ? theme.colors.blue[40] : $isFocused ? applyOpacity(theme.colors.blue[40], 10) : 'transparent'};

  &:hover {
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.blue[40] : applyOpacity(theme.colors.blue[40], 10)};
  }
`

export interface DatePickerDropdownProps<Value extends string | number> {
  className?: string
  options: { value: Value; label: string }[]
  value: Value | null
  onChange: (value: Value) => void
  placeholder?: string
  'aria-label'?: string
  style?: React.CSSProperties
}

export function DatePickerDropdown<Value extends string | number>({
  className,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  'aria-label': ariaLabel,
  style,
}: DatePickerDropdownProps<Value>) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [focusedIndex, setFocusedIndex] = React.useState(-1)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const optionsRef = React.useRef<HTMLDivElement>(null)
  const focusedOptionRef = React.useRef<HTMLDivElement>(null)
  const id = React.useId() // Generate a unique ID for the button
  const listboxId = `${id}-listbox`

  // When opening the dropdown, focus the selected option if it exists
  const handleOpen = React.useCallback(() => {
    setIsOpen(true)
    // Get the index of the currently selected option
    const selectedIndex = options.findIndex(option => option.value === value)

    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [options, value])

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }, [])

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  // Add effect to scroll focused option into view
  React.useEffect(() => {
    if (isOpen && focusedIndex >= 0 && focusedOptionRef.current && optionsRef.current) {
      const optionElement = focusedOptionRef.current
      const containerElement = optionsRef.current

      const optionTop = optionElement.offsetTop
      const optionBottom = optionTop + optionElement.offsetHeight
      const containerScrollTop = containerElement.scrollTop
      const containerHeight = containerElement.clientHeight

      if (optionBottom > containerScrollTop + containerHeight) {
        // Scroll down if option is below viewport
        containerElement.scrollTop = optionBottom - containerHeight
      } else if (optionTop < containerScrollTop) {
        // Scroll up if option is above viewport
        containerElement.scrollTop = optionTop
      }
    }
  }, [isOpen, focusedIndex])

  // Modified keyDown handler
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault()
      handleOpen()
      return
    }

    if (isOpen) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setFocusedIndex(prev => Math.min(prev + 1, options.length - 1))
          break
        case 'ArrowUp':
          event.preventDefault()
          setFocusedIndex(prev => Math.max(prev - 1, 0))
          break
        case ' ':
        case 'Enter':
          event.preventDefault()
          if (focusedIndex >= 0) {
            onChange(options[focusedIndex].value)
            setIsOpen(false)
            setFocusedIndex(-1)
          }
          break
        case 'Escape':
          event.preventDefault()
          setIsOpen(false)
          setFocusedIndex(-1)
          break
        case 'Tab':
          setIsOpen(false)
          setFocusedIndex(-1)
          break
      }
    }
  }

  const selectValue = (value != null ? options.find(option => option.value === value)?.label : null) ?? placeholder

  // Modified click handler
  const handleButtonClick = () => {
    if (!isOpen) {
      handleOpen()
    } else {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }

  return (
    <DatePickerDropdownContainer ref={containerRef} className={className} style={style}>
      <StyledButtonWithIcon
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        id={id}
        size="small"
        icon={<KeyboardArrowDownIcon size={18} />}
      >
        {selectValue}
      </StyledButtonWithIcon>
      <OptionsContainer
        $isOpen={isOpen}
        ref={optionsRef}
        role="listbox"
        id={listboxId}
        aria-labelledby={id}
        tabIndex={-1}
      >
        {options.map((option, index) => (
          <Option
            key={index}
            onClick={() => {
              onChange(option.value)
              setIsOpen(false)
            }}
            role="option"
            aria-selected={value === option.value}
            $isSelected={value === option.value}
            $isFocused={focusedIndex === index}
            ref={focusedIndex === index ? focusedOptionRef : undefined}
          >
            {option.label}
          </Option>
        ))}
      </OptionsContainer>
    </DatePickerDropdownContainer>
  )
}
